import { VStack, HStack, Text, Box, Card, Skeleton } from "@chakra-ui/react";
import { FaBell, FaEye, FaCheck, FaFilePen } from "react-icons/fa6";
import { toUserTZ } from "@hydrologiq/common/helpers/date";
import { AlertEvent } from "@hydrologiq/gql-experience/graphql";
import "./AlertTimeline.scss";

interface AlertTimelineProps {
  events: AlertEvent[];
  loading?: boolean;
}

function getEventIcon(kind: AlertEvent["kind"]) {
  switch (kind) {
    case "new":
      return FaBell;
    case "acknowledged":
      return FaEye;
    case "completed":
      return FaCheck;
    case "note":
      return FaFilePen;
    default:
      return FaBell;
  }
}

function getEventColor(kind: string) {
  switch (kind) {
    case "new":
      return "red.500";
    case "acknowledged":
      return "blue.500";
    case "completed":
      return "green.500";
    case "note":
      return "gray.500";
    default:
      return "gray.500";
  }
}

function getEventLabel(kind: string) {
  switch (kind) {
    case "new":
      return "Alert Created";
    case "acknowledged":
      return "Alert Acknowledged";
    case "attended":
      return "Alert Attended";
    case "completed":
      return "Alert Completed";
    case "note":
      return "Note Added";
    default:
      return "Event";
  }
}

function getUserDisplayName(user?: AlertEvent["user"]) {
  if (!user) return "System";

  if (user.firstName || user.lastName) {
    return `${user.firstName || ""} ${user.lastName || ""}`.trim();
  }

  if (user.email) {
    return user.email;
  }

  return "Unknown User";
}

export function AlertTimeline({ events, loading }: AlertTimelineProps) {
  if (loading) {
    return (
      <VStack gap={4} align="stretch">
        {[...Array(3)].map((_, index) => (
          <Card.Root key={index}>
            <Card.Body>
              <HStack gap={3}>
                <Skeleton width="40px" height="40px" borderRadius="full" />
                <VStack align="start" gap={1} flex={1}>
                  <Skeleton height="20px" width="150px" />
                  <Skeleton height="16px" width="100px" />
                  <Skeleton height="16px" width="200px" />
                </VStack>
              </HStack>
            </Card.Body>
          </Card.Root>
        ))}
      </VStack>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Card.Root>
        <Card.Body>
          <Text color="gray.500" textAlign="center">
            No events recorded for this alert
          </Text>
        </Card.Body>
      </Card.Root>
    );
  }

  return (
    <VStack gap={4} align="stretch" className="alert-timeline">
      {events.map((event, index) => {
        const Icon = getEventIcon(event.kind);
        const color = getEventColor(event.kind);
        const label = getEventLabel(event.kind);
        const isLast = index === events.length - 1;

        return (
          <Box key={event.id} position="relative">
            <Card.Root>
              <Card.Body>
                <HStack gap={3} align="start">
                  {/* Timeline icon */}
                  <Box
                    className="timeline-icon"
                    bg={color}
                    color="white"
                    borderRadius="full"
                    width="40px"
                    height="40px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon size={16} />
                  </Box>

                  {/* Event content */}
                  <VStack align="start" gap={1} flex={1}>
                    <HStack justify="space-between" width="100%">
                      <Text fontWeight="medium" color={color}>
                        {label}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {toUserTZ(event.created_at).format(
                          "MMM dd, yyyy HH:mm"
                        )}
                      </Text>
                    </HStack>

                    <Text fontSize="sm" color="fg.muted">
                      by {getUserDisplayName(event.user)}
                    </Text>

                    {event.note && (
                      <Text fontSize="sm" color="primary" mt={1}>
                        {event.note}
                      </Text>
                    )}
                  </VStack>
                </HStack>
              </Card.Body>
            </Card.Root>

            {/* Timeline connector line */}
            {!isLast && (
              <Box
                position="absolute"
                left="19px"
                top="60px"
                width="2px"
                height="20px"
                bg="gray.200"
                zIndex={-1}
              />
            )}
          </Box>
        );
      })}
    </VStack>
  );
}

export default AlertTimeline;
